/**
 * Inscription protégée (App Check + honeypot), sans createUser client.
 */
import { getToken } from 'firebase/app-check';
import { appCheck } from '../firebase/config';
import { warmAppCheckToken, withTimeout } from '../utils/callableHelpers';

const CREATE_URL =
  process.env.REACT_APP_CREATE_ACCOUNT_URL ||
  'https://us-central1-ckry-f7bd7.cloudfunctions.net/createUserAccountHttp';

function normalizeEmail(email) {
  return String(email || '')
    .trim()
    .replace(/\s+/g, '+')
    .toLowerCase();
}

async function primeRecaptcha() {
  if (typeof window === 'undefined') return;
  const key =
    process.env.REACT_APP_APPCHECK_SITE_KEY || process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
  const grecaptcha = window.grecaptcha;
  if (!key || !grecaptcha) return;
  try {
    if (typeof grecaptcha.ready === 'function') {
      await Promise.race([
        new Promise((resolve) => grecaptcha.ready(resolve)),
        new Promise((resolve) => setTimeout(resolve, 2500))
      ]);
    }
    if (typeof grecaptcha.execute === 'function') {
      await grecaptcha.execute(key, { action: 'signup' });
    }
  } catch (_) {
    /* App Check getToken remains the source of truth */
  }
}

async function getAppCheckHeader() {
  await primeRecaptcha();
  let token = await warmAppCheckToken(4000);
  if (!token && appCheck) {
    try {
      const result = await withTimeout(getToken(appCheck, true), 4000, 'App Check timeout');
      token = result?.token || null;
    } catch (_) {
      token = null;
    }
  }
  return token;
}

/**
 * @param {{
 *   email: string,
 *   password: string,
 *   name: string,
 *   phone?: string,
 *   website?: string,
 *   magicLinkToken?: string,
 *   acceptedTerms: boolean,
 *   formStartedAt?: number,
 *   lang?: string
 * }} opts
 */
export async function createProtectedAccount(opts) {
  const email = normalizeEmail(opts.email);
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };

  if (!opts.magicLinkToken) {
    const appCheckToken = await getAppCheckHeader();
    if (appCheckToken) {
      headers['X-Firebase-AppCheck'] = appCheckToken;
    }
  }

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 25000);
  try {
    const res = await fetch(CREATE_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        email,
        password: opts.password,
        name: opts.name,
        phone: opts.phone || '',
        website: opts.website || '',
        magicLinkToken: opts.magicLinkToken || '',
        acceptedTerms: Boolean(opts.acceptedTerms),
        formStartedAt: Number(opts.formStartedAt) || 0,
        lang: opts.lang || 'fr'
      }),
      signal: ctrl.signal
    });

    let data = null;
    try {
      data = await res.json();
    } catch (_) {
      data = null;
    }

    if (!res.ok || !data?.success) {
      const err = new Error(data?.message || 'Impossible de créer le compte');
      if (res.status === 409 || data?.code === 'already-exists') {
        err.code = 'auth/email-already-in-use';
      } else if (res.status === 429 || data?.code === 'resource-exhausted') {
        err.code = 'auth/too-many-requests';
      } else if (res.status === 400 && /app check|anti-robot|bloqueur/i.test(String(data?.message || ''))) {
        err.code = 'failed-precondition';
      } else {
        err.code = data?.code || 'send-failed';
      }
      throw err;
    }

    if (!data.customToken && !data.uid) {
      const err = new Error(data?.message || 'Impossible de créer le compte');
      err.code = 'send-failed';
      throw err;
    }

    return data;
  } catch (err) {
    if (err?.name === 'AbortError') {
      const timeoutErr = new Error('timeout');
      timeoutErr.code = 'timeout';
      throw timeoutErr;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
